// Copyright (c) 2023-2024, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com
// See license file.

// This file contains all the types used in the application.
// You will find here the types for the entities and useful function associated with them (copy operators, diff, etc).
import _ from "lodash";

export type ID = string;
export type TIME = string;
export type DATE = string;

// Member is the current user of the application
export type Member = {
  name: string;
  googleToken: string;
  token: string;
  id?: string;
  legacyID?: string;
};

// CDOMember is a member of the CDO, past or present.
export type CDOMember = {
  id: string;
  legacy_id?: string;
  display_name: string;
};

// OpenMode is the mode in which a form is opened.
export type OpenMode =
  | "Create" // Open entity's creation form
  | "Edit" // View entity - in a list -  with possibility to edit it inplace
  | "InlineList" // View entity - in a list -  with possibility to go to the entity page and remove it;
  | "ReadOnly"; // View entity - in a list - without any action possible.

// Case

export type Case = {
  __typename?: "Case";
  id: ID;
  legacy_id?: string;
  targets: Array<Reference>;
  sources: Array<Reference>;
  member_id?: string;
  case_type: CaseType;
  case_sub_type?: CaseSubType;
  case_status: "Classé" | "Actif" | "";
  case_sub_status?: CaseSubStatus;
  case_concerns_center: boolean;
  demand_receipt_date?: DATE;
  description: string;
  creation_date: TIME;
  last_update: TIME;
};

export type CaseEdit = Partial<Case>;
export type CaseCreation = Omit<
  Omit<Omit<Case, "id">, "creation_date">,
  "last_update"
>;

export type CaseType =
  | "Contrôle"
  | "Demande d'Information"
  | "Signalement"
  | "Information"
  | "Différend"
  | "Démarche Administrative"
  | "Web"
  | "Plainte";

export const caseTypeOptions = [
  "Contrôle",
  "Demande d'Information",
  "Démarche Administrative",
  "Différend",
  "Information",
  "Plainte",
  "Signalement",
  "Web",
].map((v) => ({
  text: v,
  value: v,
}));

export function isCaseTypeDemande(t: CaseType): boolean {
  return (
    t == "Demande d'Information" ||
    t == "Démarche Administrative" ||
    t == "Information" ||
    t == "Contrôle"
  );
}

export function isCaseTypePlainte(t: CaseType): boolean {
  return t == "Plainte";
}

export function isCaseTypeSignalement(t: CaseType): boolean {
  return t == "Signalement" || t == "Web";
}

export function isCaseTypeDifferent(t: CaseType): boolean {
  return t == "Différend";
}

export type CaseSubType =
  | "Soins dégradés"
  | "Demande Renseignement"
  | "Pb de Titre (Spécialiste, Plaque etc)"
  | "Autre"
  | "Factures impayées"
  | "Defaut d'information"
  | "Pub Web FB"
  | "Comportement"
  | "attente Dossier sinistres"
  | "Dissimulation"
  | "Dossier société d'exercice"
  | "Recherche Dossier"
  | "Hygiene"
  | "CPAM"
  | "Signalement"
  | "Problème avec patient"
  | "Honoraires"
  | "inscription"
  | "Pub centre"
  | "Autre département"
  | "Violence"
  | "Inscription"
  | "Refus de soins"
  | "Pub Web FB Plaque"
  | "Plainte Patient Recherche Dossier"
  | "Mutilation"
  | "AdAp"
  | "violence"
  | "Secret professionnel"
  | "Santéclair ou autre mutuelle"
  | "Escroquerie"
  | "Différend entre confrères"
  | "Demande de réinscription"
  | "Honoraires CMU"
  | "Actes Fictifs"
  | "stage actif"
  | "Non confraternité"
  | "Discrimination"
  | "Refus de soins CMU"
  | "Convention"
  | "Solidarité"
  | "Qualité / RCP";

export const caseSubTypeOptions = [
  "Actes Fictifs",
  "AdAp",
  "attente Dossier sinistres",
  "Autre département",
  "Autre",
  "Comportement",
  "Convention",
  "CPAM",
  "Defaut d'information",
  "Demande de réinscription",
  "Demande Renseignement",
  "Différend entre confrères",
  "Discrimination",
  "Dissimulation",
  "Dossier société d'exercice",
  "Escroquerie",
  "Factures impayées",
  "Honoraires CMU",
  "Honoraires",
  "Hygiene",
  "inscription",
  "Inscription",
  "Mutilation",
  "Non confraternité",
  "Pb de Titre (Spécialiste, Plaque etc)",
  "Plainte Patient Recherche Dossier",
  "Problème avec patient",
  "Pub centre",
  "Pub Web FB Plaque",
  "Pub Web FB",
  "Qualité / RCP",
  "Recherche Dossier",
  "Refus de soins CMU",
  "Refus de soins",
  "Santéclair ou autre mutuelle",
  "Secret professionnel",
  "Signalement",
  "Soins dégradés",
  "Solidarité",
  "stage actif",
  "violence",
  "Violence",
  undefined,
].map((v) =>
  v
    ? {
        text: v,
        value: v,
      }
    : { text: "-", value: undefined }
);

export const caseStatusOptions = ["Classé", "Actif"].map((v) => ({
  text: v,
  value: v,
}));

export type CaseSubStatus =
  | "Retrait de plainte"
  | "Transmission CDPI"
  | "En attente de nouvelles pièces"
  | "Autre département"
  | "Renseignements donnés"
  | "Simple signalement sans suite"
  | "Conciliation"
  | "Transmission vers RCP"
  | "Conciliation sans convocation";

export const caseSubStatusOptions = [
  "Autre département",
  "Conciliation sans convocation",
  "Conciliation",
  "En attente de nouvelles pièces",
  "Renseignements donnés",
  "Retrait de plainte",
  "Simple signalement sans suite",
  "Transmission CDPI",
  "Transmission vers RCP",
  undefined,
].map((v) =>
  v
    ? {
        text: v,
        value: v,
      }
    : { text: "-", value: undefined }
);

// Dentists

export type Dentist = {
  __typename?: "Dentist";
  id: ID;
  title: string;
  first_name: string;
  last_name: string;
  contact: ContactInformation;
  date_of_birth: string;
  cno_id: number;
  rpps_id: number | null;
  dep_id: string | null;
  cdo_id: number | null;
  notes: string;
  diploma_issuing_country: string | null;
  contact_imported: ContactInformation;
  cases: Array<string>;
};

export type DentistEdit = Partial<Dentist>;

// Actors

export type Actor = {
  __typename?: "Actor";
  id: ID;
  type: ActorType;
  title: string;
  first_name: string;
  last_name: string;
  institution_name: string | null; // DEPRECATED
  institution_finess: number | null; // DEPRECATED
  contact: ContactInformation;
  notes: string;
  cases: Array<string>;
};

export type ActorEdit = Partial<Actor>;
export type ActorCreation = Omit<Actor, "id">;

export type ContactInformation = {
  __typename?: "ContactInformation";
  email?: string;
  address?: string;
  zipcode?: string;
  city?: string;
  phone_landline?: string;
  phone_cellular?: string;
};

export type Reference = {
  name: string;
  type: string | null;
  id: string | null;
};

export type ActorType =
  | "Institution"
  | "Sécu, Mutuelles et Organismes de Santé"
  | "Ordre"
  | "Autre"
  | "Centre de Santé" // DEPRECATED
  | "Avocat"
  | "Praticien Etudiant"
  | "Prothésiste"
  | "Société Commerciale"
  | "Société d'Exercice"
  | "Patient";

export const actorTypeSelectOptions = [
  "Autre",
  "Avocat",
  // "Centre de Santé", This is another type, you shall not mutate an actor into this.
  "Institution",
  "Ordre",
  "Patient",
  "Praticien Etudiant",
  "Prothésiste",
  "Société Commerciale",
  "Société d'Exercice",
  "Sécu, Mutuelles et Organismes de Santé",
].map((v) => ({ text: v, value: v }));

// Health Centers

export type HealthCenter = {
  id: string;
  name: string;
  other_name: string;
  finess: number;
  contact: ContactInformation;
  contact_imported: ContactInformation;
  siret_number: string;
  company_finess: number;
  notes: string;
  cases: Array<string>;
};

export type HealthCenterEdit = Partial<Omit<HealthCenter, "id">>;

// Templates

export type Template = {
  __typename?: "Template";
  id: ID;
  title: string;
  category: TemplateCategory;
  sub_category: TemplateSubCategory;
  content: string;
};

export type TemplateCategory =
  | "Patient"
  | "Praticiens & Centres"
  | "Administratif";
export const templateCategorySelectOptions = [
  "Patient",
  "Praticiens & Centres",
  "Administratif",
].map((v) => ({ text: v, value: v }));
export type TemplateSubCategory =
  | "Hygiène, Communication"
  | "Honoraires"
  | "Dettes"
  | "Pub et Web"
  | "Statut d'un dossier en cours"
  | "Exercice dans le rayon d'interdiction"
  | "Refus de soigner (CMU …) ou soins négligés"
  | "Dossier médical, Feuille de soins"
  | "Demande de renseignements"
  | "Diriger vers l'assurance"
  | "Conciliation"
  | "Administratif"
  | "Demande version des faits";

export const templateSubCategorySelectOptions = [
  "Hygiène, Communication",
  "Honoraires",
  "Dettes",
  "Pub et Web",
  "Statut d'un dossier en cours",
  "Exercice dans le rayon d'interdiction",
  "Refus de soigner (CMU …) ou soins négligés",
  "Dossier médical, Feuille de soins",
  "Demande de renseignements",
  "Diriger vers l'assurance",
  "Conciliation",
  "Administratif",
  "Demande version des faits",
].map((v) => ({
  text: v,
  value: v,
}));

export type TemplateEdit = Partial<Template>;
export type TemplateCreation = Omit<Template, "id">;

// Events

export type Event = {
  __typename?: "Event";
  id: ID;
  legacy_id?: string;
  type: EventType;
  direction: EventDirection;
  document?: Document;
  document_receipt_date?: DATE;
  case_id: string;
  member_id: string;
  actor_id?: string;
  notes: string;
  creation_date: TIME;
};

export type EventEdit = Partial<Event>;
export type EventCreation = Omit<Omit<Event, "id">, "creation_date">;

export type Document = {
  title: string;
  html_content: string;
};

export type EventType =
  | "Appel Téléphonique"
  | "Autre"
  | "Courrier RAR"
  | "Courrier Simple"
  | "Email"
  | "Message sur Répondeur"
  | "RdV au CDO"
  | "Tentative de Conciliation"
  | "Transmission CDPI";

export const eventTypeSelectOptions = [
  "Appel Téléphonique",
  "Autre",
  "Courrier RAR",
  "Courrier Simple",
  "Email",
  "Message sur Répondeur",
  "RdV au CDO",
  "Tentative de Conciliation",
  "Transmission CDPI",
].map((v) => ({ text: v, value: v }));

export const pureEventTypes: Array<EventType> = [
  "RdV au CDO",
  "Tentative de Conciliation",
  "Transmission CDPI",
];

export type EventDirection = "Aucune" | "Entrant" | "Sortant";

export const eventDirectionSelectOptions = ["Aucune", "Entrant", "Sortant"].map(
  (v) => ({ text: v, value: v })
);

// Imports

export type Import = {
  id: string;
  timestamp: string;
  kind: "4D" | "FINESS";
};

//// Copy opperators ////

export function caseCopy(input: Case): Case {
  return {
    __typename: "Case",
    id: input.id,
    legacy_id: input.legacy_id,
    targets: [...input.targets],
    sources: [...input.sources],
    member_id: input.member_id,
    case_type: input.case_type,
    case_sub_type: input.case_sub_type,
    case_status: input.case_status,
    case_sub_status: input.case_sub_status,
    case_concerns_center: input.case_concerns_center,
    demand_receipt_date: input.demand_receipt_date,
    description: input.description,
    creation_date: input.creation_date,
    last_update: input.last_update,
  };
}

export function event(input: Event): Event {
  return {
    __typename: "Event",
    id: input.id,
    legacy_id: input.legacy_id,
    case_id: input.case_id,
    member_id: input.member_id,
    actor_id: input.actor_id,
    type: input.type,
    direction: input.direction,
    document: input.document ? { ...input.document } : undefined,
    document_receipt_date: input.document_receipt_date,
    notes: input.notes,
    creation_date: input.creation_date,
  };
}

export function actor(input: Actor): Actor {
  return {
    __typename: "Actor",
    id: input.id,
    type: input.type,
    title: input.title,
    first_name: input.first_name,
    last_name: input.last_name,
    institution_name: input.institution_name,
    institution_finess: input.institution_finess,
    contact: contactInformation(input.contact),
    notes: input.notes,
    cases: [...input.cases],
  };
}

export function healthCenter(input: HealthCenter): HealthCenter {
  return {
    id: input.id,
    name: input.name,
    other_name: input.other_name,
    finess: input.finess,
    contact: contactInformation(input.contact),
    contact_imported: contactInformation(input.contact_imported),
    siret_number: input.siret_number,
    company_finess: input.company_finess,
    notes: input.notes,
    cases: [...input.cases],
  };
}

export function dentist(input: Dentist): Dentist {
  return {
    __typename: "Dentist",
    id: input.id,
    dep_id: input.dep_id,
    cdo_id: input.cdo_id,
    cno_id: input.cno_id,
    rpps_id: input.rpps_id,
    date_of_birth: input.date_of_birth,
    diploma_issuing_country: input.diploma_issuing_country,
    title: input.title,
    first_name: input.first_name,
    last_name: input.last_name,
    contact: contactInformation(input.contact),
    contact_imported: contactInformation(input.contact_imported),
    notes: input.notes,
    cases: [...input.cases],
  };
}

export function contactInformation(
  input: ContactInformation
): ContactInformation {
  return {
    __typename: "ContactInformation",
    email: input.email,
    address: input.address,
    zipcode: input.zipcode,
    city: input.city,
    phone_landline: input.phone_landline,
    phone_cellular: input.phone_cellular,
  };
}

///// Diff opperator /////

// Deep diff between two objects. This will only recursively explore properties that are objects themselves.
// In this context, arrays and the likes are considred values and are not diffed, they are replaced entirely.
// TODO(alix.mougenot): Please add tests for this function.
export function diff<T extends { [key: string]: any }>(
  original: T,
  edited: T
): Partial<T> {
  return _.transform(edited, function (result: any, value, key) {
    if (!_.has(original, key)) {
      if (value !== undefined) {
        result[key] = value;
      }
    } else if (
      _.isObject(value) &&
      !_.isArray(value) &&
      !_.isMap(value) &&
      !_.isSet(value)
    ) {
      const d = diff(original[key], value);
      if (!_.isEqual(d, {})) {
        result[key] = d;
      }
    } else if (value === undefined && key in original) {
      // tell the update to delete the key (we encode remove with an empty string in JSON)
      result[key] = "";
    } else if (value !== null && !_.isEqual(value, original[key])) {
      result[key] = value;
    }
  });
}

// Type related opperations
export function referenceTo(p: Actor | Dentist | HealthCenter): Reference {
  if ("finess" in p) {
    return {
      id: p.id,
      type: "cntr",
      name: p.name,
    };
  } else if ("type" in p) {
    if (p.type === "Institution") {
      return {
        id: p.id,
        type: p.type,
        name: "l'institution " + p.last_name,
      };
    } else {
      return {
        id: p.id,
        type: p.type,
        name: p.first_name + " " + p.last_name,
      };
    }
  } else {
    return {
      id: p.id,
      type: "pract",
      name: p.first_name + " " + p.last_name,
    };
  }
}

export function pictogram(model: Actor | Dentist | HealthCenter) {
  if ("type" in model && model.type) {
    if (
      model.type == "Avocat" ||
      model.type == "Patient" ||
      model.type == "Prothésiste"
    ) {
      return "human";
    } else if (model.type == "Praticien Etudiant") {
      return "student";
    } else {
      return "institute";
    }
  } else if ("finess" in model) {
    return "institute";
  } else {
    return "practitian";
  }
}
